import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { DEFAULT_SIMULATION_PARAMS, SIMULATION_PRESETS } from '../../utils/setlistSimulationAdvanced';
import './styles.css';

/**
 * SimulationControls - Advanced controls for setlist simulation
 * 
 * Provides UI for:
 * - Preset profiles (Mainstream, Day 1, Festival, Lofi)
 * - Core dynamics (theta, sigma, beta, alpha)
 * - Randomness controls (noise model, bursts, jumps)
 * - Priority bias (keeps top 20 near top 10)
 */
export const SimulationControls = () => {
  const [params, setParams] = useState(DEFAULT_SIMULATION_PARAMS);
  const [activeTab, setActiveTab] = useState('presets');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  
  // Load saved params from Firebase on mount
  useEffect(() => {
    loadParams();
  }, []);
  
  const loadParams = async () => {
    try {
      const docRef = doc(db, 'adminSetlist', 'simulationParams');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const savedParams = docSnap.data();
        setParams({ ...DEFAULT_SIMULATION_PARAMS, ...savedParams });
      }
    } catch (error) {
      console.error('Error loading simulation params:', error);
    }
  };
  
  const saveParams = async () => {
    setSaving(true);
    setSaveMessage('');
    
    try {
      const docRef = doc(db, 'adminSetlist', 'simulationParams');
      await setDoc(docRef, params);
      
      setSaveMessage('Saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving simulation params:', error);
      setSaveMessage('Save failed');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };
  
  const applyPreset = (presetKey) => {
    const preset = SIMULATION_PRESETS[presetKey];
    if (!preset) return;
    
    setParams(prev => ({ ...prev, ...preset.params }));
    setSaveMessage(`Applied ${preset.name} preset`);
    setTimeout(() => setSaveMessage(''), 3000);
  };
  
  const resetToDefaults = () => {
    setParams(DEFAULT_SIMULATION_PARAMS);
    setSaveMessage('Reset to defaults');
    setTimeout(() => setSaveMessage(''), 3000);
  };
  
  const updateParam = (key, value) => {
    setParams(prev => ({ ...prev, [key]: value }));
  };
  
  return (
    <div className="simulation-controls">
      <div className="sim-controls-header">
        <h3>Simulation Engine Controls</h3>
        <div className="sim-controls-actions">
          <button onClick={resetToDefaults} className="sim-btn sim-btn-secondary">
            Reset
          </button>
          <button 
            onClick={saveParams} 
            disabled={saving}
            className="sim-btn sim-btn-primary"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
      
      {saveMessage && (
        <div className={`sim-save-message ${saveMessage.includes('success') || saveMessage.includes('Applied') || saveMessage.includes('Reset') ? 'success' : 'error'}`}>
          {saveMessage}
        </div>
      )}
      
      {/* Tab Navigation */}
      <div className="sim-tabs">
        <button 
          className={`sim-tab ${activeTab === 'presets' ? 'active' : ''}`}
          onClick={() => setActiveTab('presets')}
        >
          Presets
        </button>
        <button 
          className={`sim-tab ${activeTab === 'dynamics' ? 'active' : ''}`}
          onClick={() => setActiveTab('dynamics')}
        >
          Core Dynamics
        </button>
        <button 
          className={`sim-tab ${activeTab === 'randomness' ? 'active' : ''}`}
          onClick={() => setActiveTab('randomness')}
        >
          Randomness
        </button>
        <button 
          className={`sim-tab ${activeTab === 'bias' ? 'active' : ''}`}
          onClick={() => setActiveTab('bias')}
        >
          Priority Bias
        </button>
      </div>
      
      {/* Tab Content */}
      <div className="sim-tab-content">
        
        {/* PRESETS TAB */}
        {activeTab === 'presets' && (
          <div className="sim-section">
            <h4>Crowd Type Presets</h4>
            <p className="sim-description">
              Pre-configured settings that mimic different audience behaviors
            </p>
            
            <div className="preset-grid">
              {Object.entries(SIMULATION_PRESETS).map(([key, preset]) => (
                <div key={key} className="preset-card">
                  <h5>{preset.name}</h5>
                  <p>{preset.description}</p>
                  <button 
                    onClick={() => applyPreset(key)}
                    className="sim-btn sim-btn-preset"
                  >
                    Apply
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* CORE DYNAMICS TAB */}
        {activeTab === 'dynamics' && (
          <div className="sim-section">
            <h4>Core Dynamics</h4>
            <p className="sim-description">
              Controls how songs move up and down over time
            </p>
            
            <div className="controls-grid">
              {/* Ranking Mode */}
              <div className="control-item">
                <label>
                  <span className="control-label">Ranking Mode</span>
                  <span className="control-value">{params.rankingMode || 'cumulative'}</span>
                </label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="rankingMode"
                      value="cumulative"
                      checked={(params.rankingMode || 'cumulative') === 'cumulative'}
                      onChange={(e) => updateParam('rankingMode', e.target.value)}
                    />
                    <span>Cumulative (Organic growth)</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="rankingMode"
                      value="snapshot"
                      checked={params.rankingMode === 'snapshot'}
                      onChange={(e) => updateParam('rankingMode', e.target.value)}
                    />
                    <span>Snapshot (Faster changes)</span>
                  </label>
                </div>
                <p className="control-hint">Cumulative = votes accumulate over time (realistic). Snapshot = recalculated each cycle (volatile).</p>
              </div>
            
              <div className="control-item">
                <label>
                  <span className="control-label">EMA Alpha (α)</span>
                  <span className="control-value">{params.alpha.toFixed(2)}</span>
                </label>
                <input 
                  type="range" 
                  min="0.05" 
                  max="0.6" 
                  step="0.01"
                  value={params.alpha}
                  onChange={(e) => updateParam('alpha', parseFloat(e.target.value))}
                />
                <p className="control-hint">Lower = slower shifts across snapshots (only affects Snapshot mode)</p>
              </div>
              
              <div className="control-item">
                <label>
                  <span className="control-label">Mean Reversion (θ)</span>
                  <span className="control-value">{params.theta.toFixed(2)}</span>
                </label>
                <input 
                  type="range" 
                  min="0.05" 
                  max="0.6" 
                  step="0.01"
                  value={params.theta}
                  onChange={(e) => updateParam('theta', parseFloat(e.target.value))}
                />
                <p className="control-hint">Pulls songs back toward average position</p>
              </div>
              
              <div className="control-item">
                <label>
                  <span className="control-label">Volatility (σ)</span>
                  <span className="control-value">{params.sigma.toFixed(2)}</span>
                </label>
                <input 
                  type="range" 
                  min="0.05" 
                  max="0.8" 
                  step="0.01"
                  value={params.sigma}
                  onChange={(e) => updateParam('sigma', parseFloat(e.target.value))}
                />
                <p className="control-hint">How much random wiggle in movements</p>
              </div>
              
              <div className="control-item">
                <label>
                  <span className="control-label">Competition (β)</span>
                  <span className="control-value">{params.beta.toFixed(2)}</span>
                </label>
                <input 
                  type="range" 
                  min="0.5" 
                  max="4" 
                  step="0.1"
                  value={params.beta}
                  onChange={(e) => updateParam('beta', parseFloat(e.target.value))}
                />
                <p className="control-hint">Higher = sharper competition for top spots</p>
              </div>
            </div>
          </div>
        )}
        
        {/* RANDOMNESS TAB */}
        {activeTab === 'randomness' && (
          <div className="sim-section">
            <h4>Randomness Controls</h4>
            <p className="sim-description">
              Fine-tune unpredictable elements like bursts and jumps
            </p>
            
            <div className="controls-grid">
              <div className="control-item">
                <label className="control-label">Noise Model</label>
                <div className="button-group">
                  <button 
                    className={`sim-btn-small ${params.noiseModel === 'gaussian' ? 'active' : ''}`}
                    onClick={() => updateParam('noiseModel', 'gaussian')}
                  >
                    Gaussian
                  </button>
                  <button 
                    className={`sim-btn-small ${params.noiseModel === 'poisson' ? 'active' : ''}`}
                    onClick={() => updateParam('noiseModel', 'poisson')}
                  >
                    Poisson
                  </button>
                  <button 
                    className={`sim-btn-small ${params.noiseModel === 'none' ? 'active' : ''}`}
                    onClick={() => updateParam('noiseModel', 'none')}
                  >
                    None
                  </button>
                </div>
                <p className="control-hint">How votes are sampled each tick</p>
              </div>
              
              <div className="control-item">
                <label>
                  <span className="control-label">Crowd Jitter</span>
                  <span className="control-value">{(params.crowdJitterPct * 100).toFixed(0)}%</span>
                </label>
                <input 
                  type="range" 
                  min="0" 
                  max="0.6" 
                  step="0.01"
                  value={params.crowdJitterPct}
                  onChange={(e) => updateParam('crowdJitterPct', parseFloat(e.target.value))}
                />
                <p className="control-hint">Crowd size varies tick-to-tick</p>
              </div>
              
              <div className="control-item">
                <label>
                  <span className="control-label">Common Shock</span>
                  <span className="control-value">{params.commonShock.toFixed(2)}</span>
                </label>
                <input 
                  type="range" 
                  min="0" 
                  max="0.8" 
                  step="0.01"
                  value={params.commonShock}
                  onChange={(e) => updateParam('commonShock', parseFloat(e.target.value))}
                />
                <p className="control-hint">Same sway applied to all songs</p>
              </div>
              
              <div className="control-item">
                <label>
                  <span className="control-label">Probability Smoothing</span>
                  <span className="control-value">{params.probSmoothing.toFixed(2)}</span>
                </label>
                <input 
                  type="range" 
                  min="0" 
                  max="0.9" 
                  step="0.05"
                  value={params.probSmoothing}
                  onChange={(e) => updateParam('probSmoothing', parseFloat(e.target.value))}
                />
                <p className="control-hint">Stickiness of attention (0=none, 0.9=very sticky)</p>
              </div>
              
              <div className="control-item">
                <label>
                  <span className="control-label">Burst Probability</span>
                  <span className="control-value">{params.burstProbPerSec.toFixed(2)}/sec</span>
                </label>
                <input 
                  type="range" 
                  min="0" 
                  max="0.5" 
                  step="0.01"
                  value={params.burstProbPerSec}
                  onChange={(e) => updateParam('burstProbPerSec', parseFloat(e.target.value))}
                />
                <p className="control-hint">Chance of a fan wave each second</p>
              </div>
              
              <div className="control-item">
                <label>
                  <span className="control-label">Burst Size</span>
                  <span className="control-value">{params.burstMean.toFixed(0)} votes</span>
                </label>
                <input 
                  type="range" 
                  min="0" 
                  max="200" 
                  step="1"
                  value={params.burstMean}
                  onChange={(e) => updateParam('burstMean', parseFloat(e.target.value))}
                />
                <p className="control-hint">Average size of a fan wave</p>
              </div>
              
              <div className="control-item">
                <label>
                  <span className="control-label">Burst Jitter</span>
                  <span className="control-value">{(params.burstJitterPct * 100).toFixed(0)}%</span>
                </label>
                <input 
                  type="range" 
                  min="0" 
                  max="1.5" 
                  step="0.05"
                  value={params.burstJitterPct}
                  onChange={(e) => updateParam('burstJitterPct', parseFloat(e.target.value))}
                />
                <p className="control-hint">How variable burst sizes are</p>
              </div>
              
              <div className="control-item">
                <label>
                  <span className="control-label">Jump Probability</span>
                  <span className="control-value">{params.jumpProbPerSec.toFixed(2)}/sec</span>
                </label>
                <input 
                  type="range" 
                  min="0" 
                  max="0.5" 
                  step="0.01"
                  value={params.jumpProbPerSec}
                  onChange={(e) => updateParam('jumpProbPerSec', parseFloat(e.target.value))}
                />
                <p className="control-hint">Occasional sudden attention on one track</p>
              </div>
              
              <div className="control-item">
                <label>
                  <span className="control-label">Jump Magnitude</span>
                  <span className="control-value">{params.jumpStd.toFixed(2)}</span>
                </label>
                <input 
                  type="range" 
                  min="0" 
                  max="2" 
                  step="0.05"
                  value={params.jumpStd}
                  onChange={(e) => updateParam('jumpStd', parseFloat(e.target.value))}
                />
                <p className="control-hint">How strong jumps are when they happen</p>
              </div>
            </div>
          </div>
        )}
        
        {/* PRIORITY BIAS TAB */}
        {activeTab === 'bias' && (
          <div className="sim-section">
            <h4>Priority Bias (Keep Top 20 Near Top 10)</h4>
            <p className="sim-description">
              Gives your top 20 custom songs a boost to keep them dominant
            </p>
            
            <div className="controls-grid">
              <div className="control-item full-width">
                <label className="toggle-label">
                  <input 
                    type="checkbox" 
                    checked={params.biasEnabled}
                    onChange={(e) => updateParam('biasEnabled', e.target.checked)}
                  />
                  <span>Enable Priority Bias</span>
                </label>
                <p className="control-hint">Earlier titles in top 20 get higher base bias (exponential decay)</p>
              </div>
              
              {params.biasEnabled && (
                <>
                  <div className="control-item">
                    <label>
                      <span className="control-label">Bias Power</span>
                      <span className="control-value">{params.biasPower.toFixed(2)}</span>
                    </label>
                    <input 
                      type="range" 
                      min="0" 
                      max="2" 
                      step="0.05"
                      value={params.biasPower}
                      onChange={(e) => updateParam('biasPower', parseFloat(e.target.value))}
                    />
                    <p className="control-hint">Higher = stronger push for top songs</p>
                  </div>
                  
                  <div className="control-item">
                    <label>
                      <span className="control-label">Bias Decay</span>
                      <span className="control-value">{params.biasDecay.toFixed(2)}</span>
                    </label>
                    <input 
                      type="range" 
                      min="0.05" 
                      max="0.6" 
                      step="0.01"
                      value={params.biasDecay}
                      onChange={(e) => updateParam('biasDecay', parseFloat(e.target.value))}
                    />
                    <p className="control-hint">How fast bias drops down the list</p>
                  </div>
                  
                  <div className="control-item full-width">
                    <div className="bias-preview">
                      <h5>Bias Preview (Top 10)</h5>
                      <div className="bias-bars">
                        {[...Array(10)].map((_, i) => {
                          const bias = params.biasPower * Math.exp(-params.biasDecay * i);
                          const percentage = (bias / params.biasPower) * 100;
                          return (
                            <div key={i} className="bias-bar-item">
                              <span className="bias-rank">#{i + 1}</span>
                              <div className="bias-bar-container">
                                <div 
                                  className="bias-bar-fill" 
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <span className="bias-value">{bias.toFixed(2)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default SimulationControls;
